import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTeamSets,
  saveTeamSets,
  applyTeamSet,
} from '../../../../../../api/teamSets';
import {
  getCurrentStrategy,
  getStrategyTactics,
  getMyMajorSquad,
} from '../../../../../../redux/selector/Team';
import { STRATEGY_NAMES } from '../../../../../../service/constants';
import { EFFORT_LABELS } from '../../../../../../service/matchEngine';
import { toastRender } from '../../../../../../service/toaster';
import { teamGetMyStrategy } from '../../../../../../redux/actions/Team';
import { ReactComponent as CrossIcon } from '../../../../../../assets/img/cross.svg';
import {
  StyledPresets,
  StyledPresetRow,
  StyledPresetSaveRow,
  StyledPresetCopy,
  StyledPresetName,
  StyledPresetMeta,
  StyledPresetActions,
  StyledPresetButton,
  StyledPresetIconButton,
  StyledPresetInput,
  StyledPresetEmpty,
} from './styled';

const FORMATION_BY_INDEX = Object.fromEntries(
  Object.entries(STRATEGY_NAMES).map(([name, index]) => [index, name])
);
const MINDSET_LABELS = ['Defend', 'Neutral', 'Attack'];
const MAX_PRESETS = 3;
// Reasons in a 409 from POST /team/applySet. `suspended` arrives with Match Engine 2.5; anything
// unknown reads as unavailable rather than "left club".
const PRESET_UNAVAILABLE_REASONS = {
  injured: 'injured',
  suspended: 'suspended',
  not_in_team: 'left club',
};

const formationIndex = (strategy) => {
  if (Number.isFinite(Number(strategy))) return Number(strategy);
  return STRATEGY_NAMES[strategy] ?? 0;
};

const formationLabel = (value) => {
  if (typeof value === 'string' && STRATEGY_NAMES[value] != null) return value;
  return FORMATION_BY_INDEX[Number(value)] || 'Formation';
};

const presetMeta = (set) => {
  const formation = formationLabel(set.formation);
  const mindset = MINDSET_LABELS[Number(set?.tactics?.mindset)] || null;
  const effort = EFFORT_LABELS[Number(set?.tactics?.effort)] || null;
  const parts = [formation];
  if (mindset) parts.push(mindset);
  if (effort) parts.push(effort);
  if (!(Array.isArray(set.players) && set.players.length === 11)) {
    parts.push('no XI');
  }
  return parts.join(' · ');
};

const lineupPlayers = (majorStaff) =>
  (majorStaff || [])
    .filter((player) => player?._id && player.current_position)
    .map((player) => ({
      _id: player._id,
      pos: player.current_position,
    }));

export default function StrategyPresets() {
  const dispatch = useDispatch();
  const currentStrategy = useSelector(getCurrentStrategy);
  const { effort, mindset } = useSelector(getStrategyTactics);
  const majorStaff = useSelector(getMyMajorSquad);
  const [sets, setSets] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await fetchTeamSets();
      if (response.status === 200) {
        const data = await response.json();
        setSets(Array.isArray(data) ? data : data?.sets || []);
      }
    } catch {
      /* keep last */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persist = async (nextSets, successMessage = 'Preset saved.') => {
    setBusy(true);
    try {
      const response = await saveTeamSets(nextSets);
      if (response.status === 200) {
        setSets(nextSets);
        toastRender('success', successMessage);
        return true;
      }
      if (response.status === 201) {
        toastRender('error', 'You can only keep three presets.');
      } else if (response.status === 202) {
        toastRender('error', 'Preset names must be unique.');
      } else if (response.status === 409) {
        try {
          const body = await response.json();
          const names = (body.players || [])
            .map((player) => player.name || 'Unknown')
            .join(', ');
          toastRender(
            'error',
            names
              ? `Unavailable players: ${names}`
              : 'Some players are unavailable for this preset.'
          );
        } catch {
          toastRender('error', 'Some players are unavailable for this preset.');
        }
      } else {
        toastRender('error', 'Could not save preset.');
      }
      return false;
    } catch {
      toastRender('error', 'Could not save preset.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveCurrent = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toastRender('error', 'Name this preset first.');
      return;
    }
    const isUpdate = sets.some((set) => set.name === trimmed);
    if (!isUpdate && sets.length >= MAX_PRESETS) {
      toastRender('error', 'You can only keep three presets.');
      return;
    }
    const players = lineupPlayers(majorStaff);
    const entry = {
      name: trimmed,
      formation: formationIndex(currentStrategy),
      tactics: { effort, mindset },
      players: players.length === 11 ? players : [],
    };
    const without = sets.filter((set) => set.name !== trimmed);
    const next = [...without, entry];
    const ok = await persist(next);
    if (ok) setName('');
  };

  const apply = async (set) => {
    setBusy(true);
    try {
      if (Array.isArray(set.players) && set.players.length === 11) {
        const response = await applyTeamSet(
          set.name,
          set.formation == null ? undefined : formationIndex(set.formation)
        );
        if (response.status === 200) {
          const body = await response.json().catch(() => ({}));
          toastRender('success', 'Preset applied.');
          if (body.message) toastRender('success', body.message);
          dispatch(teamGetMyStrategy());
          await load();
        } else if (response.status === 409) {
          const body = await response.json().catch(() => ({}));
          const detail = (body.players || [])
            .map(
              (player) =>
                `${player.name || 'Player'} (${PRESET_UNAVAILABLE_REASONS[player.reason] || 'unavailable'})`
            )
            .join(', ');
          toastRender('error', detail || 'Preset players unavailable.');
        } else if (response.status === 400) {
          toastRender('error', 'This preset has no saved XI.');
        } else if (response.status === 403) {
          toastRender('error', 'That lineup is no longer valid.');
        } else if (response.status === 404) {
          toastRender('error', 'Preset not found.');
        } else {
          toastRender('error', 'Could not apply preset.');
        }
      } else {
        toastRender(
          'error',
          'Formation-only preset — set formation/tactics from the strategy panel.'
        );
      }
    } catch {
      toastRender('error', 'Could not apply preset.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (setName) => {
    const next = sets.filter((set) => set.name !== setName);
    await persist(next, 'Preset deleted.');
  };

  return (
    <StyledPresets>
      <StyledPresetSaveRow>
        <StyledPresetInput
          value={name}
          maxLength={32}
          placeholder="Preset name"
          onChange={(event) => setName(event.target.value)}
        />
        <StyledPresetButton
          type="button"
          disabled={busy}
          onClick={saveCurrent}
        >
          Save XI
        </StyledPresetButton>
      </StyledPresetSaveRow>
      {sets.length === 0 ? (
        <StyledPresetEmpty>No presets yet (max 3).</StyledPresetEmpty>
      ) : (
        sets.map((set) => (
          <StyledPresetRow key={set.name}>
            <StyledPresetCopy>
              <StyledPresetName>{set.name}</StyledPresetName>
              <StyledPresetMeta>{presetMeta(set)}</StyledPresetMeta>
            </StyledPresetCopy>
            <StyledPresetActions>
              <StyledPresetButton
                type="button"
                disabled={busy}
                onClick={() => apply(set)}
              >
                Apply
              </StyledPresetButton>
              <StyledPresetIconButton
                type="button"
                disabled={busy}
                aria-label={`Delete ${set.name}`}
                title="Delete preset"
                onClick={() => remove(set.name)}
              >
                <CrossIcon />
              </StyledPresetIconButton>
            </StyledPresetActions>
          </StyledPresetRow>
        ))
      )}
    </StyledPresets>
  );
}
